/**
 * One input hash per stage (D18). A stage whose upstream artifact changed reports STALE
 * and waits for an explicit rerun; pure stages (COMPILE, TIMELINE) auto-rerun. Nothing is
 * ever deleted by staleness — takes stay linked.
 */

import type { IAdProject } from "../schemas/adProject"
import { EAdFreshness, EAdStage } from "../schemas/adProject"

/** Stable, key-sorted JSON so hashing is order-independent. */
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null"
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`
}

const sha256 = async (input: string): Promise<string> => {
  const bytes = new TextEncoder().encode(input)
  const digest = await crypto.subtle.digest("SHA-256", bytes)
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
  return `sha256:${hex}`
}

/**
 * Pairs, not an object, so the *slot id* is part of the hash — and sorted, because
 * `Object.entries` preserves insertion order and re-binding a slot would otherwise re-order the
 * list and stale a stage whose inputs never changed.
 */
const bindingPairs = (doc: IAdProject, keep: (kind: string) => boolean): unknown =>
  Object.entries(doc.bindings)
    .filter(([, binding]) => keep(String(binding.kind)))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([slotId, binding]) => [slotId, binding])

const voiceSlotBinding = (doc: IAdProject): unknown => bindingPairs(doc, (kind) => kind === "voice")

const identityBindings = (doc: IAdProject): unknown => bindingPairs(doc, (kind) => kind !== "voice")

const selectedTakeIds = (
  takes: Record<string, Array<{ jobId: string }>> | undefined,
  selected: Record<string, number> | undefined,
): unknown =>
  Object.entries(selected ?? {})
    .map(([id, index]) => [id, takes?.[id]?.[index]?.jobId ?? null])
    .sort(([a], [b]) => (String(a) < String(b) ? -1 : 1))

/** The location plate's take-group id in `keyframes.takes` — a take group that is not a shot. */
export const AD_ENV_PLATE_GROUP_ID = "env_plate"

/**
 * The job id of the plate take the operator has selected, or `undefined` when there is none.
 * Exported because KEYFRAME's staleness is a function of it and the service has to be able to ask
 * whether a poll filled it in for the first time or replaced an existing pick.
 */
export const selectedPlateJobId = (doc: IAdProject): string | undefined => {
  const index = doc.keyframes?.selected[AD_ENV_PLATE_GROUP_ID]
  return index === undefined ? undefined : doc.keyframes?.takes[AD_ENV_PLATE_GROUP_ID]?.[index]?.jobId
}

/** The upstream artifact each stage is a function of (spec §3.5 stage input table). */
const upstreamOf = (doc: IAdProject, stage: EAdStage): unknown => {
  switch (stage) {
    case EAdStage.SCRIPT:
      return { formatId: doc.formatId, bindings: doc.bindings, context: doc.context, override: doc.overrides.script }
    case EAdStage.SPEECH:
      return {
        lines: (doc.script?.lines ?? []).map((line) => [line.id, line.text]),
        voice: voiceSlotBinding(doc),
      }
    case EAdStage.SHOTS:
      return {
        script: doc.script?.lines ?? [],
        durations: Object.entries(doc.speech?.lines ?? {})
          .map(([lineId, line]) => [lineId, line.durationSec ?? null])
          .sort(([a], [b]) => (String(a) < String(b) ? -1 : 1)),
        override: doc.overrides.shots,
      }
    case EAdStage.KEYFRAME:
      return {
        generationProfile: doc.generationProfile,
        shots: (doc.shots ?? []).map((shot) => [shot.id, shot.prompt, shot.framing]),
        bindings: identityBindings(doc),
        anchorTakeId: doc.keyframes?.anchorTakeId ?? null,
        // Every shot references the selected location plate, so picking another one stales them.
        // `undefined` when there is no plate drops out of the hash, leaving plate-less docs FRESH.
        plate: selectedPlateJobId(doc),
      }
    case EAdStage.COMPILE:
      // `buildClipParams`/`estimateUsd` read the speech line's `audioKey` and measured duration, so
      // a re-voiced line changes the plan even when the shots and keyframes are untouched.
      return {
        generationProfile: doc.generationProfile,
        keyframes: selectedTakeIds(doc.keyframes?.takes, doc.keyframes?.selected),
        shots: doc.shots ?? [],
        speech: doc.speech?.lines ?? {},
      }
    case EAdStage.GENERATE:
      return { plan: doc.plan ?? null }
    case EAdStage.TIMELINE:
      // Assembly reads the shots (cut lengths) and the plan's `multi_prompt` slot boundaries as
      // well as the selected takes — editing a shot must stale the timeline built from it.
      return {
        video: selectedTakeIds(doc.video?.takes, doc.video?.selected),
        speech: doc.speech?.lines ?? {},
        shots: doc.shots ?? [],
        plan: doc.plan ?? null,
        // Pacing decides where the timeline cuts, so flipping it must stale TIMELINE (and RENDER
        // through it). `undefined` drops out of the hash, so pre-pacing docs stay FRESH.
        pacing: doc.pacing,
      }
    case EAdStage.RENDER:
      return { timelineHash: doc.stages.timeline?.inputHash ?? null }
    default:
      return null
  }
}

export const stageInputHash = async (doc: IAdProject, stage: EAdStage): Promise<string> =>
  sha256(`${stage}|${stableStringify(upstreamOf(doc, stage))}`)

/** True when the stage has produced nothing yet. */
const hasArtifact = (doc: IAdProject, stage: EAdStage): boolean => {
  switch (stage) {
    case EAdStage.SCRIPT:
      return (doc.script?.lines.length ?? 0) > 0
    case EAdStage.SPEECH:
      return Object.keys(doc.speech?.lines ?? {}).length > 0
    case EAdStage.SHOTS:
      return (doc.shots?.length ?? 0) > 0
    case EAdStage.KEYFRAME:
      return Object.keys(doc.keyframes?.takes ?? {}).length > 0
    case EAdStage.COMPILE:
      return (doc.plan?.clips.length ?? 0) > 0
    case EAdStage.GENERATE:
      return Object.keys(doc.video?.takes ?? {}).length > 0
    case EAdStage.TIMELINE:
    case EAdStage.RENDER:
      return Boolean(doc.stages[stage]?.lastRunAt)
    default:
      return false
  }
}

export const isStale = async (doc: IAdProject, stage: EAdStage): Promise<EAdFreshness> => {
  if (!hasArtifact(doc, stage)) return EAdFreshness.EMPTY
  const recorded = doc.stages[stage]?.inputHash
  if (!recorded) return EAdFreshness.STALE
  return recorded === (await stageInputHash(doc, stage)) ? EAdFreshness.FRESH : EAdFreshness.STALE
}

export const freshnessForAllStages = async (doc: IAdProject): Promise<Record<EAdStage, EAdFreshness>> => {
  const entries = await Promise.all(
    Object.values(EAdStage).map(async (stage) => [stage, await isStale(doc, stage)] as const),
  )
  return Object.fromEntries(entries) as Record<EAdStage, EAdFreshness>
}
